import { useCallback, useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import { RefreshCw, Eye, MousePointerClick, TrendingUp, Award } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import TablaSeo from './seo/TablaSeo';
import KeywordsObjetivo from './seo/KeywordsObjetivo';
import { Variacion, fmtFecha, fmtNum, fmtPct, fmtPos } from './seo/seoUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const RANGOS = [
  { label: 'Últimos 7 días', valor: '7' },
  { label: 'Últimos 28 días', valor: '28' },
  { label: 'Últimos 3 meses', valor: '90' },
  { label: 'Fechas a elegir', valor: 'fechas' },
];

const GRIS = '#9CA3AF';

function KpiCard({ label, value, icon: Icon, hint, variacion }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-gray-900 flex items-baseline gap-2">
        {value}
        {variacion}
      </div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

// Colores por punto: gris en los días provisionales
const colorPuntos = (serie, color) => serie.map((d) => (d.preliminar ? GRIS : color));
const segmentoPreliminar = (serie, color) => ({
  borderColor: (ctx) => (serie[ctx.p1DataIndex]?.preliminar ? GRIS : color),
  borderDash: (ctx) => (serie[ctx.p1DataIndex]?.preliminar ? [4, 4] : undefined),
});

export default function MarketingWebSeo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rango, setRango] = useState('28');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [incluirProvisionales, setIncluirProvisionales] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  const [tabActiva, setTabActiva] = useState('consultas');
  const [kw, setKw] = useState({ keywords: [], sugerencias: [] });
  const [kwCargando, setKwCargando] = useState(true);

  const estado = incluirProvisionales ? 'all' : 'final';

  const cargar = useCallback(async () => {
    if (rango === 'fechas' && (!fechaDesde || !fechaHasta)) return;
    setLoading(true);
    setError(null);
    try {
      const params = rango === 'fechas'
        ? { desde: fechaDesde, hasta: fechaHasta, estado }
        : { dias: parseInt(rango, 10), estado };
      const res = await marketingWebAPI.seo(params);
      setData(res.data);
    } catch (e) {
      console.error('Error SEO:', e);
      setError(e?.response?.data?.detail || 'Error cargando los datos de Search Console');
    } finally {
      setLoading(false);
    }
  }, [rango, fechaDesde, fechaHasta, estado]);

  const cargarKeywords = useCallback(async () => {
    setKwCargando(true);
    try {
      const res = await marketingWebAPI.seoKeywordsObjetivo();
      setKw(res.data || { keywords: [], sugerencias: [] });
    } catch (e) {
      console.error('Error palabras clave objetivo:', e);
    } finally {
      setKwCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarKeywords(); }, [cargarKeywords]);

  const sync = async () => {
    setSyncing(true);
    setAviso(null);
    try {
      const res = await marketingWebAPI.seoSync(14);
      const r = res.data || {};
      setAviso(`Sincronizado: ${r.dias_sincronizados ?? 0} días, ${r.keywords_objetivo ?? 0} palabras clave objetivo, ${fmtNum(r.filas_pedidas)} filas pedidas a Search Console.`);
      await Promise.all([cargar(), cargarKeywords()]);
    } catch (e) {
      console.error('Error sincronizando Search Console:', e);
      setError(e?.response?.data?.detail || 'Error sincronizando con Search Console');
    } finally {
      setSyncing(false);
    }
  };

  const crearKeyword = async (payload) => {
    try {
      await marketingWebAPI.seoKeywordObjetivoCrear(payload);
      setAviso(`"${payload.keyword}" añadida. Su historial de los últimos 28 días tarda unos segundos en aparecer.`);
      await cargarKeywords();
      // El historial inicial se pide en segundo plano: recargamos al poco
      setTimeout(() => { cargarKeywords(); }, 6000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se ha podido añadir la palabra clave');
      throw e;
    }
  };

  const editarKeyword = async (id, payload) => {
    try {
      await marketingWebAPI.seoKeywordObjetivoEditar(id, payload);
      await cargarKeywords();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se ha podido guardar');
      throw e;
    }
  };

  const borrarKeyword = async (k) => {
    if (!window.confirm(`¿Dejar de seguir "${k.keyword}"? Se borra también su historial.`)) return;
    try {
      await marketingWebAPI.seoKeywordObjetivoBorrar(k.id);
      await cargarKeywords();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se ha podido borrar');
    }
  };

  const seguirDesdeTabla = async (keyword, pagina) => {
    try {
      await crearKeyword({ keyword, pagina_objetivo: pagina || null });
    } catch (e) {
      // el error ya se muestra arriba
    }
  };

  const keywordsSeguidas = useMemo(() => new Set((kw.keywords || []).map((k) => k.keyword)), [kw]);

  const serie = useMemo(() => data?.serie || [], [data]);

  const lineChart = useMemo(() => {
    if (!serie.length) return null;
    return {
      labels: serie.map((d) => fmtFecha(d.fecha).slice(0, 5)),
      datasets: [
        {
          label: 'Impresiones',
          data: serie.map((d) => d.total_impressions || 0),
          borderColor: '#1A9169',
          backgroundColor: 'rgba(26, 145, 105, 0.12)',
          pointBackgroundColor: colorPuntos(serie, '#1A9169'),
          segment: segmentoPreliminar(serie, '#1A9169'),
          fill: true,
          tension: 0.3,
          yAxisID: 'y',
        },
        {
          label: 'Clics',
          data: serie.map((d) => d.total_clicks || 0),
          borderColor: '#D7977E',
          backgroundColor: 'rgba(215, 151, 126, 0.10)',
          pointBackgroundColor: colorPuntos(serie, '#D7977E'),
          segment: segmentoPreliminar(serie, '#D7977E'),
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
        },
      ],
    };
  }, [serie]);

  const positionChart = useMemo(() => {
    if (!serie.length) return null;
    return {
      labels: serie.map((d) => fmtFecha(d.fecha).slice(0, 5)),
      datasets: [{
        label: 'Posición media',
        // Días sin impresiones: sin punto (null), nunca posición 0
        data: serie.map((d) => (d.total_impressions > 0 && d.avg_position ? d.avg_position : null)),
        borderColor: '#6CA9AE',
        backgroundColor: 'rgba(108, 169, 174, 0.15)',
        pointBackgroundColor: colorPuntos(serie, '#6CA9AE'),
        segment: segmentoPreliminar(serie, '#6CA9AE'),
        spanGaps: true,
        fill: false,
        tension: 0.3,
      }],
    };
  }, [serie]);

  const hayPreliminares = serie.some((d) => d.preliminar);

  const tooltipSerie = {
    callbacks: {
      title: (items) => {
        const d = serie[items[0]?.dataIndex];
        if (!d) return '';
        return `${fmtFecha(d.fecha)}${d.preliminar ? ' (provisional)' : ''}`;
      },
    },
  };

  const positionMensualChart = useMemo(() => {
    if (!data?.serie_mensual?.length) return null;
    return {
      labels: data.serie_mensual.map((m) => m.mes),
      datasets: [{
        label: 'Posición media mensual',
        data: data.serie_mensual.map((m) => m.position_media || null),
        borderColor: '#1A9169',
        backgroundColor: 'rgba(26, 145, 105, 0.15)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
      }],
    };
  }, [data]);

  const traficoMensualChart = useMemo(() => {
    if (!data?.serie_mensual?.length) return null;
    return {
      labels: data.serie_mensual.map((m) => m.mes),
      datasets: [
        {
          label: 'Impresiones',
          data: data.serie_mensual.map((m) => m.impressions || 0),
          borderColor: '#6CA9AE',
          backgroundColor: 'rgba(108, 169, 174, 0.12)',
          fill: true,
          tension: 0.3,
          yAxisID: 'y',
        },
        {
          label: 'Clics',
          data: data.serie_mensual.map((m) => m.clicks || 0),
          borderColor: '#D7977E',
          backgroundColor: 'rgba(215, 151, 126, 0.10)',
          fill: false,
          tension: 0.3,
          yAxisID: 'y1',
        },
      ],
    };
  }, [data]);

  const ejePosicion = {
    reverse: true,
    beginAtZero: false,
    suggestedMin: 1,
    title: { display: true, text: 'Posición (1 = primero)' },
  };

  const tot = data?.totales;
  const ant = data?.totales_anteriores;
  const periodoAnteriorTxt = data?.periodo_anterior
    ? `${fmtFecha(data.periodo_anterior.desde)} a ${fmtFecha(data.periodo_anterior.hasta)}`
    : null;
  const vsAnterior = periodoAnteriorTxt ? `Frente a ${periodoAnteriorTxt}` : null;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary">SEO · Search Console</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cómo aparece <span className="font-mono">duettocatering.com</span> en Google.
            {data && (
              <> Periodo: <strong>{fmtFecha(data.desde)}</strong> a <strong>{fmtFecha(data.hasta)}</strong>.</>
            )}
            {' '}Google tarda 2 o 3 días en consolidar los datos.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={rango}
            onChange={(e) => setRango(e.target.value)}
            className="px-3 py-2 text-sm border rounded-md"
          >
            {RANGOS.map((r) => (
              <option key={r.valor} value={r.valor}>{r.label}</option>
            ))}
          </select>
          {rango === 'fechas' && (
            <>
              <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="px-2 py-1.5 text-sm border rounded-md" />
              <span className="text-sm text-gray-500">a</span>
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="px-2 py-1.5 text-sm border rounded-md" />
            </>
          )}
          <label className="flex items-center gap-1.5 text-sm text-gray-600" title="Incluye los últimos días, que Google todavía puede corregir">
            <input type="checkbox" checked={incluirProvisionales} onChange={(e) => setIncluirProvisionales(e.target.checked)} />
            Incluir días provisionales
          </label>
          <button
            onClick={cargar}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </button>
          <button
            onClick={sync}
            disabled={syncing}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50"
            title="Vuelve a pedir a Search Console los últimos 14 días y la posición de las palabras clave objetivo"
          >
            {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-yellow-700 hover:underline">Cerrar</button>
        </div>
      )}
      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-4 text-sm text-emerald-800 flex justify-between gap-2">
          <span>{aviso}</span>
          <button type="button" onClick={() => setAviso(null)} className="text-emerald-700 hover:underline">Cerrar</button>
        </div>
      )}
      {data?.error_gsc && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 text-sm text-yellow-800">
          {data.error_gsc} Revisa que la cuenta de servicio tiene acceso a la propiedad en Search Console y que GSC_SITE_URL es correcto.
        </div>
      )}

      {rango === 'fechas' && (!fechaDesde || !fechaHasta) && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 text-sm text-blue-800">
          Elige la fecha de inicio y la de fin.
        </div>
      )}

      {data && (
        <>
          {/* KPIs del periodo con comparación */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard
              label="Impresiones"
              value={fmtNum(tot?.impressions)}
              icon={Eye}
              hint={vsAnterior ? `${vsAnterior}: ${fmtNum(ant?.impressions)}` : 'Veces que salimos en Google'}
              variacion={ant ? <Variacion valor={(tot?.impressions || 0) - (ant.impressions || 0)} /> : null}
            />
            <KpiCard
              label="Clics"
              value={fmtNum(tot?.clicks)}
              icon={MousePointerClick}
              hint={vsAnterior ? `${vsAnterior}: ${fmtNum(ant?.clicks)}` : 'Visitas que llegan desde Google'}
              variacion={ant ? <Variacion valor={(tot?.clicks || 0) - (ant.clicks || 0)} /> : null}
            />
            <KpiCard
              label="CTR medio"
              value={fmtPct(tot?.ctr)}
              icon={TrendingUp}
              hint="Porcentaje de impresiones que acaban en clic"
            />
            <KpiCard
              label="Posición media"
              value={fmtPos(tot?.position_media)}
              icon={Award}
              hint={ant?.position_media ? `1 = primero · antes ${fmtPos(ant.position_media)}` : '1 = primero, menor es mejor'}
              variacion={ant?.position_media && tot?.position_media
                ? <Variacion valor={Math.round((ant.position_media - tot.position_media) * 10) / 10} tipo="posicion" />
                : null}
            />
          </div>
        </>
      )}

      {/* Tendencia mensual arriba: es lo primero que se quiere ver */}
      {data && (
        <>
          {/* Tendencia MENSUAL (histórico completo) */}
          {(positionMensualChart || traficoMensualChart) && (
            <div className="bg-white border rounded-lg p-5 mb-6">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h2 className="text-base font-semibold text-gray-700">Tendencia mensual · todo el histórico</h2>
                <span className="text-xs text-gray-500">Posición ponderada por impresiones</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                La media diaria se mueve mucho: los últimos días son provisionales y cada contenido nuevo empieza
                apareciendo en posiciones bajas. La <strong>tendencia mensual</strong> refleja mejor la evolución real.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {positionMensualChart && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Posición media mensual (1 = primero)</h3>
                    <div className="h-72">
                      <Line
                        data={positionMensualChart}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { y: ejePosicion },
                        }}
                      />
                    </div>
                  </div>
                )}
                {traficoMensualChart && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-2">Impresiones y clics mensuales</h3>
                    <div className="h-72">
                      <Line
                        data={traficoMensualChart}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          interaction: { mode: 'index', intersect: false },
                          plugins: { legend: { position: 'top' } },
                          scales: {
                            y: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Impresiones' } },
                            y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'Clics' } },
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <KeywordsObjetivo
        items={kw.keywords || []}
        sugerencias={kw.sugerencias || []}
        cargando={kwCargando}
        onCrear={crearKeyword}
        onEditar={editarKeyword}
        onBorrar={borrarKeyword}
      />

      {data && (
        <>
          {/* Consultas y páginas del periodo (directas de Search Console) */}
          <div className="bg-white border rounded-lg p-5 mb-6">
            <div className="flex items-center justify-between gap-4 mb-1 flex-wrap">
              <h2 className="text-base font-semibold text-gray-700">
                {tabActiva === 'consultas' ? 'Consultas con las que aparecemos en Google' : 'Páginas que aparecen en Google'}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setTabActiva('consultas')}
                  className={`px-3 py-1.5 text-sm rounded-md ${tabActiva === 'consultas' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Consultas ({fmtNum(data.consultas?.length)})
                </button>
                <button
                  onClick={() => setTabActiva('paginas')}
                  className={`px-3 py-1.5 text-sm rounded-md ${tabActiva === 'paginas' ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Páginas ({fmtNum(data.paginas?.length)})
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Datos del periodo completo pedidos a Search Console
              {data.estado === 'all' ? ', incluidos los días provisionales' : ', solo días ya consolidados'}.
              {' '}Google oculta por privacidad las búsquedas muy poco frecuentes, así que la suma de la tabla puede quedar por debajo del total.
            </p>
            <TablaSeo
              key={`${tabActiva}-${data.desde}-${data.hasta}-${data.estado}`}
              tipo={tabActiva}
              filas={tabActiva === 'consultas' ? data.consultas : data.paginas}
              periodo={{ desde: data.desde, hasta: data.hasta, estado: data.estado, periodoAnterior: periodoAnteriorTxt }}
              keywordsSeguidas={keywordsSeguidas}
              onSeguir={seguirDesdeTabla}
            />
          </div>

          {/* Gráficas diarias */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {lineChart && (
              <div className="bg-white border rounded-lg p-5">
                <h2 className="text-base font-semibold mb-1 text-gray-700">Impresiones y clics por día</h2>
                {hayPreliminares && (
                  <p className="text-xs text-gray-500 mb-2">En gris, los últimos días: son provisionales y Google aún los completa.</p>
                )}
                <div className="h-72">
                  <Line
                    data={lineChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: { legend: { position: 'top' }, tooltip: tooltipSerie },
                      scales: {
                        y: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Impresiones' } },
                        y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'Clics' } },
                      },
                    }}
                  />
                </div>
              </div>
            )}
            {positionChart && (
              <div className="bg-white border rounded-lg p-5">
                <h2 className="text-base font-semibold mb-1 text-gray-700">Posición media por día (1 = primero)</h2>
                <p className="text-xs text-gray-500 mb-2">
                  Cuanto más arriba, mejor. Los días sin impresiones no se pintan{hayPreliminares ? ' y los provisionales van en gris' : ''}.
                </p>
                <div className="h-72">
                  <Line
                    data={positionChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false }, tooltip: tooltipSerie },
                      scales: { y: ejePosicion },
                    }}
                  />
                </div>
              </div>
            )}
          </div>

        </>
      )}

      {!data && !loading && !error && rango !== 'fechas' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-6 text-center text-sm text-blue-800">
          Aún no hay datos. Pulsa <strong>Sincronizar ahora</strong> para traer los últimos 14 días desde Search Console.
        </div>
      )}
    </div>
  );
}
